/** Controlled <ChatBox> (ADR 0007) wired to the threads API; the conversations side panel shows unless the
 *  top-bar header has collapsed it (agentChat.panelCollapsed — APP-3397). */

import { useCallback, useEffect, useMemo, useRef } from "react"
import type { FormEvent, KeyboardEvent, ReactNode, RefAttributes } from "react"
import { Alert, Box, Button, LinearProgress, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { ChatBox, chatMessageListClasses } from "@mui/x-chat"
import type { ChatBoxSlots, ChatBoxSlotProps, ChatMessageListProps } from "@mui/x-chat"
import type { ChatPartRendererMap, MessageListRootHandle } from "@mui/x-chat-headless"
import { AI_DISCLAIMER } from "helpers/ai"
import { AI_TEAMMATE_EVENTS, PANEL_SURFACES, THREAD_LIST_SURFACES } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { closeSkillsPane, collapsePanel, expandPanel, openSkillsPane, selectDockPane, selectPanelCollapsed, selectSurfaceView, setSurfaceView } from "../store/agent-chat-slice"
import { ArtifactPart } from "./artifact-part"
import { ArtifactPanelRegion } from "./artifact-panel-region"
import { ArtifactsSurface } from "./artifacts-surface"
import { ToolPart } from "./tool-part"
import { ChatSidePanel } from "./side-panel"
import { SkillsPageContent } from "../skills/skills-page-content"
import { CollapsedRail } from "./collapsed-rail"
import { hasPermanentDrawer, useChatLayout } from "./chat-layout-context"
import { ChatEmptyState } from "./empty-state"
import { ReattachIndicator } from "./reattach-indicator"
import { ComposerControlsContext, ComposerToolbarWithHint, handleComposerKeyDown, COMPOSER_HINT_ID, COMPOSER_PLACEHOLDER, COMPOSER_CHROME_SX, DISCLAIMER_STRIP, CONVERSATION_COLUMN_MAX_WIDTH, SCROLLBAR_GUTTER, TRANSCRIPT_CHROME_BOTTOM } from "./composer-chrome"
import type { ComposerControlsValue } from "./composer-chrome"
import { ComposerSendStopButton } from "./composer-send-stop"
import { ComposerDraftBridge } from "./composer-draft-bridge"
import { ComposerAttachmentsProvider } from "./composer-attachments-bridge"
import { KellyComposerAttachButton, KellyComposerAttachmentList, KellyComposerTextArea } from "./composer-attachments"
import { useAttachmentsConfig } from "./attachments-config"
import { LastAssistantMessageContext, MESSAGE_PRESENTATION_SX, MessageChromeActions, MessageContentWithWorkingFiles } from "./message-chrome"
import { findLastAssistantMessageId } from "./history-to-messages"
import type { ThreadRowCallbacks } from "./thread-list-item"
import { useKellyChat, useKellyChatSurfacePresence } from "./kelly-chat-context"
import { WorkingCue, isTurnVisibleInTranscript, shouldShowWorkingCue } from "./working-cue"
import { TRANSCRIPT_AT_BOTTOM_BUFFER_PX, useTranscriptScroll } from "./use-transcript-scroll"

// conversationHeader off (we render our own side-panel chrome).
const CONTROLLED_FEATURES = { conversationList: false, conversationHeader: false } as const
// Without a custom emptyState slot ChatBox renders its own "no messages" panel, which stacks under the error Alert.
const RenderNothing = () => null
// The toolbar is always mounted inside ChatBox's composer, so it hosts the zero-DOM draft bridge (APP-3404) —
// which needs useChatComposer — alongside the send controls ChatBox passes as children.
function ComposerToolbar({ children }: { children?: ReactNode }) {
  return (
    <ComposerAttachmentsProvider>
      <ComposerToolbarWithHint>
        {children}
        <ComposerDraftBridge />
      </ComposerToolbarWithHint>
    </ComposerAttachmentsProvider>
  )
}
// Two slot sets: the branded empty state is suppressed while a history error shows (they must not stack).
// avatar/authorName/inlineMeta null: managed agent, no sender label or in-bubble time (legacy parity).
const MESSAGE_SLOTS = { messageContent: MessageContentWithWorkingFiles, messageActions: MessageChromeActions, messageAvatar: null, messageAuthorName: null, messageInlineMeta: null } as const
const CHROME_SLOTS = {
  composerToolbar: ComposerToolbar,
  composerInput: KellyComposerTextArea,
  composerSendButton: ComposerSendStopButton,
  composerAttachButton: KellyComposerAttachButton,
  composerAttachmentList: KellyComposerAttachmentList,
  ...MESSAGE_SLOTS,
} satisfies ChatBoxSlots
const SLOTS = { ...CHROME_SLOTS, emptyState: ChatEmptyState } satisfies ChatBoxSlots
const SLOTS_WITHOUT_EMPTY_STATE = { ...CHROME_SLOTS, emptyState: RenderNothing } satisfies ChatBoxSlots

// Exported so the chrome story drives the same renderer wiring as production.
export const PART_RENDERERS: ChatPartRendererMap = {
  "dynamic-tool": ToolPart,
  "data-artifact": ArtifactPart,
  "data-attachment": RenderNothing,
  file: RenderNothing,
}

const ROOT_SX = { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 } satisfies SxProps<Theme>
const BODY_SX = { display: "flex", flex: 1, minHeight: 0 } satisfies SxProps<Theme>
const MAIN_SX = { flex: 1, display: "flex", flexDirection: "column", minHeight: 0, position: "relative" } satisfies SxProps<Theme>
// Holds the chat column while the Skills pane covers it: `inert` here rather than on the whole surface, so the
// conversations nav stays operable and is the way back (ADR 0029).
const CHAT_STACK_SX = { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 } satisfies SxProps<Theme>
// fontSize:inherit undoes ChatBox's 12px reset. isolation:auto drops the library's stacking context so the
// composer (z2) and the footer band (z1, a ChatBox sibling) share one context — else the band covers the composer.
// overflowAnchor none: this scroll position already has two explicit restorers (ours and the library's own
// resize anchor) — Chrome's implicit one would be a third, and Safari has none, so it can only diverge by engine.
// Not reproduced by any test here; suppressed on the writer-count argument, not a measured failure
// (ADR 0020 section 7).
export const CHATBOX_SX = [
  { flex: 1, minHeight: 0, fontSize: "inherit", isolation: "auto", [`& .${chatMessageListClasses.scroller}`]: { overflowAnchor: "none" } },
  MESSAGE_PRESENTATION_SX,
  COMPOSER_CHROME_SX,
] satisfies SxProps<Theme>
const LOAD_OLDER_SX = { alignSelf: "center", m: 1 } satisfies SxProps<Theme>
// Opaque footer band spanning card-bottom→page-bottom (height = DISCLAIMER_STRIP), never above the card, so the
// conversation scrolls behind the card. z1 (above scroller, below card z2); pointerEvents:none keeps the scrollbar usable.
const DISCLAIMER_SX = {
  position: "absolute",
  insetInline: SCROLLBAR_GUTTER,
  bottom: 0,
  marginInline: "auto",
  width: "auto",
  maxWidth: CONVERSATION_COLUMN_MAX_WIDTH,
  height: DISCLAIMER_STRIP,
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "var(--opcolor-grey-darker)",
  fontSize: "0.8rem",
  backgroundColor: "var(--app-bg-paper)",
  // Any interactive element added to this band (a link/tooltip) won't receive clicks — the band ignores the pointer so the scrollbar behind it stays usable.
  pointerEvents: "none",
} satisfies SxProps<Theme>

/**
 * Mounted only while the transcript is, so the controller's surface presence tracks ChatBox's lifetime
 * rather than ConversationSurface's. The Artifacts view unmounts ChatBox while this component stays
 * mounted; without this the MUI runtime's abort would fire with no matching re-probe on the way back,
 * and the per-thread resume latch would leave the turn frozen (ADR 0018).
 */
function TranscriptPresence() {
  useKellyChatSurfacePresence()
  return null
}

export function ConversationSurface() {
  // The single controller is hosted by KellyChatProvider (APP-3369) so the top-bar tab strip reads the same
  // source; this surface consumes the full controller and the header consumes only the tabs slice.
  const {
    threads,
    isThreadsLoaded,
    isThreadsErrored,
    activeThreadId,
    navKey,
    messages,
    onMessagesChange,
    adapter,
    selectThread,
    startNewThread,
    newChatSeq,
    stopServerStream,
    isTurnActive,
    isReattaching,
    isReconnecting,
    isProviderRetrying,
    hasOlder,
    isLoadingHistory,
    isLoadingOlder,
    historyError,
    loadOlder,
    renameThread,
    pinThread,
    canPinThreads,
    deleteThread,
    mutatingThreadId,
  } = useKellyChat()

  // This surface's mount IS the turn's lifetime, and its unmount is what the MUI runtime aborts on (ADR 0018).

  // Collapse lives in RTK (APP-3397); the controls are the panel's own collapse button and the rail's Expand,
  // both owned by this surface. A layout with a permanent drawer gets the page's chrome and writes the
  // preference; a docked dock gets neither, because its conversations list is the host's slide-over on ephemeral
  // local state and `panelCollapsed` must not be reachable from it
  // (ADR 0019 section 4, narrowed by ADR 0020 section 8).
  const dispatch = useAppDispatch()
  const layout = useChatLayout()
  const hasDrawer = hasPermanentDrawer(layout)
  const panelCollapsed = useAppSelector(selectPanelCollapsed)
  const isCollapsed = !hasDrawer || panelCollapsed
  const surfaceView = useAppSelector(selectSurfaceView)
  const isArtifactsView = surfaceView === "artifacts"

  // Focus follows only USER-initiated toggles: each button records its intent before dispatching, and the effect
  // moves focus to whichever control swapped in. Artifact-driven collapses (APP-3413) set no intent, so focus
  // stays with the artifact panel; a null intent also covers mount / route re-entry.
  const collapseButtonRef = useRef<HTMLButtonElement>(null)
  const openButtonRef = useRef<HTMLButtonElement>(null)
  const pendingFocusRef = useRef<null | "collapsed" | "expanded">(null)
  useEffect(() => {
    const pending = pendingFocusRef.current
    pendingFocusRef.current = null
    if (pending === "collapsed") openButtonRef.current?.focus()
    else if (pending === "expanded") collapseButtonRef.current?.focus()
  }, [isCollapsed])

  // The user's own toggles; the ones an opening artifact causes are tracked in artifact-panel-region.
  const analytics = useAnalytics()
  const handleCollapse = useCallback(() => {
    pendingFocusRef.current = "collapsed"
    dispatch(collapsePanel())
    analytics.track(AI_TEAMMATE_EVENTS.PANEL_COLLAPSED, PANEL_SURFACES.PAGE)
  }, [dispatch, analytics])
  const handleExpand = useCallback(() => {
    pendingFocusRef.current = "expanded"
    dispatch(expandPanel())
    analytics.track(AI_TEAMMATE_EVENTS.PANEL_EXPANDED, PANEL_SURFACES.PAGE)
  }, [dispatch, analytics])

  // Block the send while reattaching, or while a chip carries an upload error. The disabled button covers the
  // mouse; these two cover Enter and every other submit. Refs keep both handlers stable.
  const blockSubmitRef = useRef(false)
  const attachmentBlockedRef = useRef(false)
  useEffect(() => {
    blockSubmitRef.current = isReattaching
  }, [isReattaching])
  const isSubmitBlocked = useCallback(() => blockSubmitRef.current || attachmentBlockedRef.current, [])
  const onComposerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => handleComposerKeyDown(event, { blockSubmit: isSubmitBlocked() }),
    [isSubmitBlocked],
  )
  const onComposerSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      if (isSubmitBlocked()) event.preventDefault()
    },
    [isSubmitBlocked],
  )
  // The dock's view only. Collapsing the drawer swings this COLUMN ~212px but does not reflow the transcript:
  // CONVERSATION_COLUMN_MAX_WIDTH caps it at 690px and both widths are above the cap (ADR 0020 section 7).
  const { containerRef, listRef } = useTranscriptScroll(navKey, layout.mode === "dock" ? layout.view : "page")

  // Jumping to the latest message unmounts the affordance, so move focus off it first or the next Tab restarts
  // from the top of the page. Chained before the library's own handler by mergeReactProps.
  const focusLatestMessage = useCallback(() => listRef.current?.focusMessage(), [listRef])

  // ChatBoxContent spreads these straight onto <ChatMessageList>, whose ref is the MessageListRootHandle the pin
  // drives; `ref` isn't part of the slot's own prop type, hence the intersection.
  // autoScroll.buffer is passed rather than defaulted so the library's "pinned to the newest message" and ours
  // are one number. Two would leave a band where it follows a stream and we anchor them elsewhere. Note this
  // object also WINS over `features.autoScroll: false` — consumer slot props are spread last — so turning
  // auto-scroll off would have to drop this too.
  const messageListSlotProps = useMemo<Partial<ChatMessageListProps> & RefAttributes<MessageListRootHandle>>(
    () => ({ ref: listRef, autoScroll: { buffer: TRANSCRIPT_AT_BOTTOM_BUFFER_PX } }),
    [listRef],
  )

  const attachmentsConfig = useAttachmentsConfig()
  const chatFeatures = useMemo(() => ({ ...CONTROLLED_FEATURES, attachments: attachmentsConfig }), [attachmentsConfig])

  const chatSlotProps = useMemo<ChatBoxSlotProps>(
    () => ({
      composerRoot: { features: { attachments: attachmentsConfig }, onSubmit: onComposerSubmit },
      composerInput: { onKeyDown: onComposerKeyDown, "aria-describedby": COMPOSER_HINT_ID, placeholder: COMPOSER_PLACEHOLDER },
      messageList: messageListSlotProps,
      // The library pins its affordance 8px off the list bottom, under our floating composer. Right-aligned so
      // it holds still when the working cue takes the centre of the same band.
      scrollToBottom: {
        onClick: focusLatestMessage,
        sx: { bottom: TRANSCRIPT_CHROME_BOTTOM, left: "auto", right: SCROLLBAR_GUTTER, transform: "none" },
      },
    }),
    [onComposerKeyDown, onComposerSubmit, messageListSlotProps, focusLatestMessage, attachmentsConfig],
  )

  const composerControls = useMemo<ComposerControlsValue>(
    () => ({ isReattaching, stopServerStream, attachmentBlockedRef }),
    [isReattaching, stopServerStream],
  )

  // The last assistant message keeps its action toolbar visible at rest (APP-3404); a stable id string
  // means action slots re-render only when the last turn changes, not on every streamed token.
  const lastAssistantId = useMemo(() => findLastAssistantMessageId(messages), [messages])

  // The turn being written: the last assistant message only while it is still the tail of the transcript.
  // Once the reply settles and the reader sends again, the tail is their prompt and the cue has nothing to label.
  const liveAssistantMessage = useMemo(() => {
    const last = messages[messages.length - 1]
    return last != null && last.id === lastAssistantId ? last : null
  }, [messages, lastAssistantId])

  const isWorking = shouldShowWorkingCue({
    isTurnActive,
    isTurnInThisView: isTurnVisibleInTranscript(messages),
    isReattaching,
    isReconnecting,
  })

  // Picking a conversation or starting a new one leaves the Skills pane: both are requests for the transcript,
  // and the pane covers the column it would appear in.
  const rowCallbacks = useMemo<ThreadRowCallbacks>(
    () => ({
      onSelect: (threadId: string) => { dispatch(closeSkillsPane()); selectThread(threadId) },
      onRename: renameThread,
      onPin: pinThread,
      onDelete: deleteThread,
    }),
    [dispatch, selectThread, renameThread, pinThread, deleteThread],
  )
  const onStartNewThread = useCallback(() => { dispatch(closeSkillsPane()); startNewThread() }, [dispatch, startNewThread])

  // Kelly's surface navigation, handed to whichever side panel is mounted. The Artifacts view replaces the
  // transcript column and nothing else — the conversations panel, the dock and the artifact panel are untouched.
  const openArtifacts = useCallback(() => {
    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACTS_SURFACE_OPENED, PANEL_SURFACES.PAGE)
    dispatch(closeSkillsPane())
    dispatch(setSurfaceView("artifacts"))
  }, [analytics, dispatch])
  const showConversation = useCallback(() => dispatch(setSurfaceView("conversation")), [dispatch])
  const surfaceNav = useMemo(
    () => ({ isArtifactsActive: isArtifactsView, onOpenArtifacts: openArtifacts, onShowConversation: showConversation }),
    [isArtifactsView, openArtifacts, showConversation],
  )

  const panelContentProps = useMemo(
    () => ({
      threads,
      activeThreadId,
      canPinThreads,
      isThreadsLoaded,
      isThreadsErrored,
      mutatingThreadId,
      callbacks: rowCallbacks,
      onStartNewThread,
    }),
    [threads, activeThreadId, canPinThreads, isThreadsLoaded, isThreadsErrored, mutatingThreadId, rowCallbacks, onStartNewThread],
  )
  const isSkillsOpen = useAppSelector(selectDockPane) === "skills"
  const skills = useMemo(() => ({ isOpen: isSkillsOpen, onOpen: () => dispatch(openSkillsPane()) }), [isSkillsOpen, dispatch])

  return (
    <Box sx={ROOT_SX}>
      <Box sx={BODY_SX}>
        {/* No rail while docked: 52px for two buttons DockHeader already carries — 16% of the 320px floor, and
            redundant rather than merely expensive at any width. Full screen composes like the page did. */}
        {!hasDrawer ? null : isCollapsed ? (
          <CollapsedRail onOpen={handleExpand} openButtonRef={openButtonRef} skills={skills} surfaceNav={surfaceNav} />
        ) : (
          <ChatSidePanel {...panelContentProps} surface={THREAD_LIST_SURFACES.PAGE} onCollapse={handleCollapse} collapseButtonRef={collapseButtonRef} surfaceNav={surfaceNav} skills={skills} />
        )}
        <Box sx={MAIN_SX} ref={containerRef}>
          <Box sx={CHAT_STACK_SX} inert={isSkillsOpen}>
            <ArtifactPanelRegion activeThreadId={activeThreadId} newChatSeq={newChatSeq}>
              {/* Inside the region, not beside it: an artifact opened from the list docks in the same panel a
                  card opens, at the same width, with the same Esc ladder. */}
              {isArtifactsView ? <ArtifactsSurface /> : (<>
              <TranscriptPresence />
              {historyError != null && (
                <Alert severity="error" data-testid="chatbox-history-error">
                  {historyError}
                </Alert>
              )}
              {hasOlder && (
                <Button sx={LOAD_OLDER_SX} size="small" onClick={loadOlder} disabled={isLoadingOlder} data-testid="chatbox-load-older">
                  Load older messages
                </Button>
              )}
              {isLoadingHistory && <LinearProgress data-testid="chatbox-history-loading" aria-label="Loading conversation" />}
              <ComposerControlsContext.Provider value={composerControls}>
                <LastAssistantMessageContext.Provider value={lastAssistantId}>
                  <ChatBox
                    key={navKey}
                    adapter={adapter}
                    messages={messages}
                    onMessagesChange={onMessagesChange}
                    partRenderers={PART_RENDERERS}
                    features={chatFeatures}
                    slots={historyError != null ? SLOTS_WITHOUT_EMPTY_STATE : SLOTS}
                    slotProps={chatSlotProps}
                    sx={CHATBOX_SX}
                  />
                </LastAssistantMessageContext.Provider>
              </ComposerControlsContext.Provider>
              <WorkingCue isActive={isWorking} message={liveAssistantMessage} isProviderRetrying={isProviderRetrying} />
              {isReconnecting && <ReattachIndicator />}
              <Typography variant="caption" component="p" sx={DISCLAIMER_SX}>{AI_DISCLAIMER}</Typography>
              </>)}
            </ArtifactPanelRegion>
          </Box>
          {isSkillsOpen && <SkillsPageContent layout={hasDrawer ? "two-column" : "one-column"} />}
        </Box>
      </Box>
    </Box>
  )
}
